import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getReports } from "@/lib/admin-extra.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/reports")({
  component: ReportsPage,
});

const money = (n: number) => "R" + Number(n ?? 0).toLocaleString("en-ZA");

function isoDay(d: Date) {
  return d.toISOString().slice(0, 10);
}

function csv(rows: Record<string, unknown>[], name: string) {
  if (rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([body], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ReportsPage() {
  const fn = useServerFn(getReports);
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDay(d);
  }, []);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(isoDay(new Date()));

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["admin", "reports", from, to],
    queryFn: () => fn({ data: { from: new Date(from).toISOString(), to: new Date(`${to}T23:59:59`).toISOString() } }),
  });

  const maxDay = Math.max(1, ...(data?.byDay ?? []).map((d) => d.revenue));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </div>
        {isFetching && <Loader2 className="mb-2 h-4 w-4 animate-spin text-muted-foreground" />}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => csv((data?.bookings ?? []) as never, "appointments-report")}>
            <Download className="mr-2 h-4 w-4" />
            Appointments CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => csv((data?.orders ?? []) as never, "orders-report")}>
            <Download className="mr-2 h-4 w-4" />
            Orders CSV
          </Button>
        </div>
      </div>

      {isError && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Appointments" value={String(data?.totals.appointments ?? 0)} />
        <Stat label="Appointment revenue" value={money(data?.totals.appointmentRevenue ?? 0)} />
        <Stat label="Shop orders paid" value={String(data?.totals.shopOrders ?? 0)} />
        <Stat label="Shop revenue" value={money(data?.totals.shopRevenue ?? 0)} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Revenue by day</CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.byDay ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No revenue in this period.</p>
          ) : (
            <div className="flex h-40 items-end gap-1">
              {data!.byDay.map((d) => (
                <div key={d.date} className="flex-1" title={`${d.date}: ${money(d.revenue)}`}>
                  <div
                    className="w-full rounded-t bg-primary/80"
                    style={{ height: `${(d.revenue / maxDay) * 100}%`, minHeight: d.revenue > 0 ? 2 : 0 }}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>By service</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.byService ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No data.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data!.byService.map((s) => (
                  <li key={s.name} className="flex justify-between">
                    <span>
                      {s.name} <span className="text-muted-foreground">({s.count})</span>
                    </span>
                    <span>{money(s.revenue)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Payment methods</CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.byMethod ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No data.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data!.byMethod.map((m) => (
                  <li key={m.name} className="flex justify-between">
                    <span className="capitalize">{m.name}</span>
                    <span className="text-muted-foreground">{m.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}
